import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import { useCurrentTheme } from 'app/shared/theme';
import { getDateParts } from 'app/shared/utils/datetime';
import { Event } from 'app/shared/typings';
import { Box, Typography } from 'app/shared/components/atoms';
import { ActionButton } from 'app/shared/components/molecules';
import { neighborhoodOrSecretLocation } from 'app/fan/utils';
import { ReactComponent as Link } from 'icons/link.svg';

interface GuestShareLinkProps {
  event: Event;
  ticketPurchaserName?: string;
  ticketPurchaserEventAttendeeId?: string;
  marginTop: number;
  buttonMarginTop: number;
  fullWidthButton?: boolean;
  shareTextShade?: 'light' | 'dark';
  'data-qaid'?: string;
}

export const GuestShareLink: React.FC<
  React.PropsWithChildren<GuestShareLinkProps>
> = ({
  children,
  event,
  ticketPurchaserName,
  ticketPurchaserEventAttendeeId,
  marginTop,
  buttonMarginTop,
  fullWidthButton = false,
  shareTextShade = 'light',
  'data-qaid': qaId = 'event-page-guest-share-link-section',
}) => {
  const { formatMessage } = useIntl();
  const [copiedShareLink, setCopiedShareLink] = useState<boolean>(false);
  const { colors } = useCurrentTheme();

  const shareLinkDefaultText = formatMessage({
    id: 'event.attendeeDetailsForm.guestShareLinkInfoText',
  });

  const shareLinkCopiedText = formatMessage({
    id: 'event.attendeeDetailsForm.guestShareLinkInfoCopied',
  });

  const copyButtonDefaultText = formatMessage({
    id: 'event.attendeeDetailsForm.guestShareLinkText',
  });

  const copyButtonCopiedText = formatMessage({
    id: 'event.attendeeDetailsForm.guestShareLinkTextCopied',
  });

  async function copyLinkToClipboard() {
    const { month, day } = getDateParts(event.localStartsAt || '');

    const link = `${window.location.host}/events/${event.id}?eaid=${ticketPurchaserEventAttendeeId}`;

    const eventNeighborhoodTitle = neighborhoodOrSecretLocation(
      event,
      formatMessage
    );

    const shareableMessage = ticketPurchaserName
      ? formatMessage(
          {
            id: 'event.attendeeDetailsForm.guestShareLinkMessage',
          },
          {
            ticketPurchaserName,
            eventNeighborhoodTitle,
            month,
            day,
            link,
          }
        )
      : formatMessage(
          {
            id: 'event.attendeeDetailsForm.guestShareLinkMessageNoName',
          },
          {
            eventNeighborhoodTitle,
            month,
            day,
            link,
          }
        );

    setCopiedShareLink(true);

    setTimeout(() => {
      setCopiedShareLink(false);
    }, 5000);

    navigator?.clipboard?.writeText(shareableMessage);
  }

  return (
    <Box data-qaid={qaId} mt={marginTop}>
      <Typography
        color={
          shareTextShade === 'light' ? colors.primary.white.base : undefined
        }
        fontSize="fontSize100"
      >
        {copiedShareLink ? shareLinkCopiedText : shareLinkDefaultText}
      </Typography>
      <Box
        flexDirection="row"
        justifyContent="flex-start"
        alignItems="baseline"
        gap={4}
        flexWrap="wrap"
        w="fit-content"
        mt={buttonMarginTop}
      >
        <ActionButton
          variant="primary"
          onClick={copyLinkToClipboard}
          fullWidth={fullWidthButton}
          cursor="copy"
          disabled={copiedShareLink}
          leadingIcon={Link}
        >
          {copiedShareLink ? copyButtonCopiedText : copyButtonDefaultText}
        </ActionButton>
        {children}
      </Box>
    </Box>
  );
};
